import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Video, Image, X,
  Plus, ArrowLeft,
  Sparkle, Globe,
  RssSimple, ArrowClockwise,
  Download,
  SquaresFour, ListBullets,
  Article, CalendarBlank, Images
} from '@phosphor-icons/react';
import ContentDetail from './ContentDetailV3';
import MasterContentDetail from './MasterContentDetail';
import { isCanonicalMasterContent } from '../lib/masterContent';
import { resolveMasterProjectErrorMessage } from '../lib/masterProjectErrors';
import ContentCardsView from './project-view/ContentCardsView';
import ContentListView from './project-view/ContentListView';
import ContentCalendarView from './project-view/ContentCalendarView';
import FeedStrips from './project-view/FeedStrips';
import RightPanelContent from './project-view/RightPanelContent';
import DnaWorkspace from './project-view/DnaWorkspace';
import ConnectionsWorkspace from './project-view/ConnectionsWorkspace';
import MediaLibraryTab from './project-view/MediaLibraryTab';
import Analytics from './Analytics';
import ContextHelpButton from './ContextHelpButton';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const REEL_FORMATS = new Set(['reel', 'prompted_reel']);

const SOCIAL_CONTENT_FILTERS = [
  { id: 'all', icon: SquaresFour, labelKey: 'project.content.filters.all' },
  { id: 'calendar', icon: CalendarBlank, labelKey: 'project.content.filters.calendar' },
  { id: 'carousels', icon: Images, labelKey: 'project.content.filters.carousels' },
  { id: 'reels', icon: Video, labelKey: 'project.content.filters.reels' },
  { id: 'posts', icon: Article, labelKey: 'project.content.filters.posts' },
];

const MASTER_CONTENT_FILTERS = [
  { id: 'content_master', icon: Article, labelKey: 'project.content.filters.contentMaster' },
  { id: 'calendar', icon: CalendarBlank, labelKey: 'project.content.filters.calendar' },
];

function filterContentsBySubTab(allContents, subTab) {
  if (subTab === 'carousels') return allContents.filter(c => c.format === 'carousel');
  if (subTab === 'reels') return allContents.filter(c => REEL_FORMATS.has(c.format));
  if (subTab === 'posts') return allContents.filter(c => c.format === 'post');
  return allContents;
}

export function resolveSelectedContentAfterRefresh(current, newContents) {
  if (!current) return current;
  return (newContents || []).find(item => item.id === current.id) || current;
}

export function checkCarouselJobTransitions(newContents, statusRef, t) {
  for (const item of newContents || []) {
    const job = item?.carousel_render_job;
    if (!job || !job.job_id || !job.status) continue;
    const key = item.id;
    const prev = statusRef.current[key];
    const wasObservedProcessing = Boolean(prev) && prev.jobId === job.job_id && prev.status === 'processing';
    if (job.status === 'processing') {
      statusRef.current[key] = { jobId: job.job_id, status: 'processing' };
      continue;
    }
    if (wasObservedProcessing) {
      const label = item.hook_text || t('project.content.untitled');
      if (job.status === 'done') {
        toast.success(t('editor.carouselGenerationDoneNotification', { title: label }));
      } else if (job.status === 'failed') {
        toast.error(t('editor.carouselGenerationFailedNotification', { title: label, message: job.error || '' }));
      }
    }
    statusRef.current[key] = { jobId: job.job_id, status: job.status };
  }
}

export default function ProjectView({ project, setActiveView, activeTab, initialSubTab }) {
  const { api, planLimits, user } = useAuth();
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const isMasterProject = project.project_mode === 'master';
  const canAnalytics = Boolean(planLimits?.can_analytics || user?.role === 'admin' || ['osmel@osmelfabre.it', 'osmel.fabre@gmail.com'].includes(user?.email || ''));
  const isInternalFeedTester = user?.role === 'admin' || ['osmel@osmelfabre.it', 'osmel.fabre@gmail.com'].includes(user?.email || '');
  const canRssFeeds = isInternalFeedTester;
  const lastFeedLanguageRef = useRef(null);
  const carouselJobStatusRef = useRef({});
  const tRef = useRef(t);
  tRef.current = t;
  const [primaryTab, setPrimaryTab] = useState(activeTab || 'content');
  const [contentSubTab, setContentSubTab] = useState(isMasterProject ? 'content_master' : 'all');
  const [contents, setContents] = useState([]);
  const [contentsLoadError, setContentsLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedContent, setSelectedContent] = useState(null);

  const [showNewPost, setShowNewPost] = useState(false);
  const [newPostHook, setNewPostHook] = useState('');
  const [newPostFormat, setNewPostFormat] = useState('reel');
  const [newPostUseAi, setNewPostUseAi] = useState(false);
  const [newPostLoading, setNewPostLoading] = useState(false);

  const [feeds, setFeeds] = useState([]);
  const [feedItems, setFeedItems] = useState([]);
  const [feedItemsLoading, setFeedItemsLoading] = useState(true);
  const [feedItemsError, setFeedItemsError] = useState(false);
  const [feedLoading, setFeedLoading] = useState(false);
  const [aiFeedItems, setAiFeedItems] = useState([]);
  const [refreshingFeeds, setRefreshingFeeds] = useState(false);
  const [refreshingAi, setRefreshingAi] = useState(false);

  const [deletingContentId, setDeletingContentId] = useState(null);
  const [cancellingQueueId, setCancellingQueueId] = useState(null);
  const [clearingQueue, setClearingQueue] = useState(false);

  const [viewMode, setViewMode] = useState('cards');
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  const [selectedFeedItem, setSelectedFeedItem] = useState(null);
  const [pinnedItemIds, setPinnedItemIds] = useState(new Set());
  const [pinningItemId, setPinningItemId] = useState(null);

  const [queueItems, setQueueItems] = useState([]);
  const [rightPanelWidth, setRightPanelWidth] = useState(280);
  const [showRightPanel, setShowRightPanel] = useState(false);

  const [dragContent, setDragContent] = useState(null);

  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const lockedFeedPlaceholders = [1, 2, 3];

  const [publishQuota, setPublishQuota] = useState(null);
  useEffect(() => {
    if (contentSubTab !== 'calendar') return;
    api.get('/publish/quota').then(r => setPublishQuota(r.data)).catch(() => {});
  }, [contentSubTab, queueItems, api]);

  useEffect(() => {
    if (activeTab === 'analytics') {
      if (canAnalytics) {
        setPrimaryTab('analytics');
        setShowRightPanel(false);
      }
    } else if (activeTab) {
      setPrimaryTab(activeTab);
    }
    if (initialSubTab === 'calendar') {
      setContentSubTab('calendar');
    }
  }, [activeTab, initialSubTab, canAnalytics]);

  useEffect(() => {
    if (primaryTab === 'analytics' && showRightPanel) {
      setShowRightPanel(false);
    }
  }, [primaryTab, showRightPanel]);

  useEffect(() => {
    if (!canAnalytics && showRightPanel) {
      setShowRightPanel(false);
    }
  }, [canAnalytics, showRightPanel]);

  const loadContents = useCallback(() => {
    if (!project?.id) return Promise.resolve();
    return api.get(`/contents/${project.id}`)
      .then(r => {
        checkCarouselJobTransitions(r.data, carouselJobStatusRef, tRef.current);
        setContents(r.data);
        setSelectedContent(prev => resolveSelectedContentAfterRefresh(prev, r.data));
        setContentsLoadError(null);
      })
      .catch(e => { setContentsLoadError(e); });
  }, [api, project?.id]);

  useEffect(() => {
    if (!project?.id) return;
    const requests = [
      loadContents(),
      api.get(`/publish/queue/${project.id}`).then(r => setQueueItems(r.data)).catch(() => {}),
    ];
    if (canRssFeeds) {
      requests.push(
        api.get(`/feeds/${project.id}`).then(r => setFeeds(r.data)).catch(() => {}),
        api.get(`/feeds/${project.id}/pinned`).then(r => setPinnedItemIds(new Set(r.data.map(p => p.item_id)))).catch(() => {}),
        api.get(`/feeds/${project.id}/items`)
          .then(r => { setFeedItems(Array.isArray(r.data) ? r.data : []); setFeedItemsError(false); })
          .catch(() => { setFeedItemsError(true); })
          .finally(() => setFeedItemsLoading(false)),
        api.get(`/feeds/ai-suggestions/${project.id}/cached`).then(r => setAiFeedItems(r.data || [])).catch(() => {})
      );
    } else {
      setFeeds([]);
      setFeedItems([]);
      setFeedItemsError(false);
      setFeedItemsLoading(false);
      setAiFeedItems([]);
      setPinnedItemIds(new Set());
    }
    Promise.all(requests).finally(() => setLoading(false));
  }, [api, project?.id, canRssFeeds, loadContents]);

  useEffect(() => {
    if (!project?.id || loading || !canRssFeeds) return;
    const interval = setInterval(() => {
      api.post(`/feeds/refresh/${project.id}`).then(() => {
        api.get(`/feeds/${project.id}/items`)
          .then(r => { setFeedItems(Array.isArray(r.data) ? r.data : []); setFeedItemsError(false); })
          .catch(() => {});
      }).catch(() => {});
      // Only RSS items poll in the background. AI suggestions are cache-only
      // on entry and language change; only an explicit user action invokes AI.
    }, 600000);
    return () => clearInterval(interval);
  }, [api, project?.id, loading, canRssFeeds]);

  useEffect(() => {
    if (!project?.id || loading) return;
    const interval = setInterval(() => {
      api.get(`/contents/${project.id}`).then(r => {
        checkCarouselJobTransitions(r.data, carouselJobStatusRef, tRef.current);
        setContents(r.data);
        setSelectedContent(prev => resolveSelectedContentAfterRefresh(prev, r.data));
        setContentsLoadError(null);
      }).catch(() => {});
      api.get(`/publish/queue/${project.id}`).then(r => setQueueItems(r.data)).catch(() => {});
    }, 8000);
    return () => clearInterval(interval);
  }, [api, project?.id, loading]);

  useEffect(() => {
    if (!project?.id || loading || !canRssFeeds) return;
    api.post(`/feeds/bootstrap/${project.id}`)
      .then(({ data }) => {
        if (Array.isArray(data)) {
          setFeeds(data);
          if (data.length > 0) {
            return api.get(`/feeds/${project.id}/items`).then(r => setFeedItems(r.data)).catch(() => {});
          }
        }
      })
      .catch(() => {});
  }, [api, project?.id, loading, canRssFeeds]);

  useEffect(() => {
    if (!project?.id || loading || !canRssFeeds) return;
    const currentLang = (i18n.language || 'it').toLowerCase().split('-', 1)[0];
    if (lastFeedLanguageRef.current === currentLang) return;
    lastFeedLanguageRef.current = currentLang;

    let cancelled = false;
    const reloadLocalizedFeeds = async () => {
      try {
        const { data: bootstrapped } = await api.post(`/feeds/bootstrap/${project.id}`);
        if (!cancelled && Array.isArray(bootstrapped)) {
          setFeeds(bootstrapped);
        }
        await api.post(`/feeds/refresh/${project.id}`);
        const { data: rssItems } = await api.get(`/feeds/${project.id}/items`);
        if (!cancelled) {
          setFeedItems(rssItems || []);
        }
        const { data: aiItems } = await api.get(`/feeds/ai-suggestions/${project.id}/cached`);
        if (!cancelled) {
          setAiFeedItems(aiItems || []);
        }
      } catch {
      }
    };

    reloadLocalizedFeeds();
    return () => { cancelled = true; };
  }, [api, i18n.language, loading, project?.id, canRssFeeds]);

  const openContentDetail = (c) => setSelectedContent(c);
  const handleMasterDerived = (derived) => {
    setContents(prev => [derived, ...prev.filter(item => item.id !== derived.id)]);
    setSelectedContent(derived);
  };
  const handleContentUpdate = (updated) => {
    const job = updated?.carousel_render_job;
    if (job?.job_id && job?.status) {
      carouselJobStatusRef.current[updated.id] = { jobId: job.job_id, status: job.status };
    }
    setContents(prev => prev.map(c => c.id === updated.id ? updated : c));
    setSelectedContent(prev => (prev?.id === updated.id ? updated : prev));
  };

  const deleteContent = async (id) => {
    if (!window.confirm(t('project.content.deleteConfirm'))) return;
    setDeletingContentId(id);
    const tid = toast.loading(t('project.content.deleting'));
    try {
      await api.delete(`/contents/${id}`);
      setContents(prev => prev.filter(c => c.id !== id));
      toast.success(t('project.content.deleteSuccess'), { id: tid });
    } catch (e) { toast.error(t('project.content.deleteError'), { id: tid }); }
    setDeletingContentId(null);
  };

  const createNewPost = async () => {
    if (!newPostHook.trim()) return;
    setNewPostLoading(true);
    const tid = toast.loading(t('project.content.createLoading'));
    try {
      const { data } = await api.post('/content/create-post', { project_id: project.id, hook_text: newPostHook, format: newPostFormat, use_ai: newPostUseAi });
      setContents(prev => [...prev, data]);
      setShowNewPost(false); setNewPostHook('');
      toast.success(t('project.content.createSuccess'), { id: tid });
      openContentDetail(data);
    } catch (e) { toast.error(`${t('common.error')}: ${e.response?.data?.detail || e.message}`, { id: tid }); }
    finally { setNewPostLoading(false); }
  };

  const feedToPost = async (item) => {
    if (!canRssFeeds) return;
    setFeedLoading(true);
    const tid = toast.loading(t('project.content.feedGenerating'));
    try {
      const { data } = await api.post('/feeds/generate-content', {
        project_id: project.id,
        item_id: item.id,
        item_type: item._type || (item.source === 'ai' ? 'ai' : 'rss'),
        feed_item_title: item.title,
        feed_item_summary: item.summary,
      });
      setContents(prev => [...prev, data]);
      toast.success(t('project.content.feedGenerated'), { id: tid });
      openContentDetail(data);
    } catch (e) { toast.error(`${t('common.error')}: ${e.response?.data?.detail || e.message}`, { id: tid }); }
    finally { setFeedLoading(false); }
  };

  const handleDrop = async (dayOffset, e) => {
    e.preventDefault();
    if (!dragContent) return;
    await api.put(`/contents/${dragContent.id}`, { day_offset: dayOffset });
    setContents(prev => prev.map(c => c.id === dragContent.id ? { ...c, day_offset: dayOffset } : c));
    setDragContent(null);
  };

  const moveScheduledEvent = async (event, targetDate) => {
    const movableItems = (event.items || []).filter(item => item.status === 'queued');
    if (!event?.scheduledAt || movableItems.length === 0) return;

    const nextDate = new Date(targetDate);
    nextDate.setHours(
      event.scheduledAt.getHours(),
      event.scheduledAt.getMinutes(),
      event.scheduledAt.getSeconds(),
      event.scheduledAt.getMilliseconds()
    );
    const nextIso = nextDate.toISOString();
    const previousItems = queueItems;
    setQueueItems(prev => prev.map(item =>
      movableItems.some(movable => movable.id === item.id)
        ? { ...item, scheduled_at: nextIso }
        : item
    ));

    const tid = toast.loading(t('project.calendar.moving'));
    try {
      await Promise.all(movableItems.map(item => api.put(`/publish/queue/${item.id}`, { scheduled_at: nextIso })));
      toast.success(t('project.calendar.moveSuccess'), { id: tid });
    } catch (e) {
      setQueueItems(previousItems);
      toast.error(t('project.calendar.moveError', { message: e.response?.data?.detail || e.message }), { id: tid });
    }
  };

  const moveMasterContentDayOffset = async (content, targetDate) => {
    const anchor = new Date(project.created_at || Date.now());
    anchor.setHours(0, 0, 0, 0);
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);
    const dayOffset = Math.round((target - anchor) / 86400000);
    const previousContents = contents;
    setContents(prev => prev.map(c => c.id === content.id ? { ...c, day_offset: dayOffset } : c));
    try {
      await api.put(`/contents/${content.id}`, { day_offset: dayOffset });
    } catch (e) {
      setContents(previousContents);
      toast.error(t('project.calendar.moveError', { message: e.response?.data?.detail || e.message }));
    }
  };

  const cancelQueueItem = async (id) => {
    setCancellingQueueId(id);
    const tid = toast.loading(t('project.queue.canceling'));
    try {
      await api.delete(`/publish/queue/${id}`);
      setQueueItems(prev => prev.filter(q => q.id !== id));
      toast.success(t('project.queue.cancelSuccess'), { id: tid });
    } catch (e) { toast.error(t('project.queue.cancelError'), { id: tid }); }
    setCancellingQueueId(null);
  };

  const clearQueue = async () => {
    if (queueItems.length === 0) return;
    if (!window.confirm(t('project.queue.clearConfirm'))) return;

    setClearingQueue(true);
    const tid = toast.loading(t('project.queue.clearing'));
    try {
      const activeStatuses = new Set(['queued', 'processing']);
      await api.delete(`/publish/queue/project/${project.id}`);
      setQueueItems(prev => prev.filter(item => activeStatuses.has(item.status)));
      toast.success(t('project.queue.clearSuccess'), { id: tid });
    } catch (e) {
      toast.error(t('project.queue.clearError'), { id: tid });
    }
    setClearingQueue(false);
  };

  const toggleUrgent = async (c) => {
    const newVal = !c.urgent;
    setContents(prev => prev.map(x => x.id === c.id ? { ...x, urgent: newVal } : x));
    try { await api.put(`/contents/${c.id}`, { urgent: newVal }); }
    catch { setContents(prev => prev.map(x => x.id === c.id ? { ...x, urgent: c.urgent } : x)); }
  };

  const toggleGroupCollapse = (status) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status); else next.add(status);
      return next;
    });
  };

  const pinFeedItem = async (item, itemType) => {
    const itemId = item.id;
    setPinningItemId(itemId);
    try {
      const { data } = await api.post(`/feeds/items/${itemId}/pin`, {
        project_id: project.id,
        item_data: item,
        item_type: itemType
      });
      setPinnedItemIds(prev => {
        const next = new Set(prev);
        if (data.pinned) { next.add(itemId); toast.success(t('project.feeds.pinSaved')); }
        else { next.delete(itemId); toast.success(t('project.feeds.pinRemoved')); }
        return next;
      });
    } catch { toast.error(t('project.feeds.pinError')); }
    setPinningItemId(null);
  };

  const refreshFeeds = async () => {
    if (!canRssFeeds) return;
    setRefreshingFeeds(true);
    const tid = toast.loading(t('project.feeds.refreshLoading'));
    try {
      const { data: bootstrapped } = await api.post(`/feeds/bootstrap/${project.id}`);
      if (Array.isArray(bootstrapped)) {
        setFeeds(bootstrapped);
      }
      await api.post(`/feeds/refresh/${project.id}`);
      const response = await api.get(`/feeds/${project.id}/items`);
      setFeedItems(Array.isArray(response.data) ? response.data : []);
      setFeedItemsError(false);
      toast.success(t('project.feeds.refreshSuccess'), { id: tid });
    } catch {
      setFeedItemsError(true);
      toast.error(t('project.feeds.refreshError'), { id: tid });
    } finally {
      setRefreshingFeeds(false);
    }
  };

  const refreshAiSuggestions = async () => {
    if (!canRssFeeds) return;
    setRefreshingAi(true);
    const tid = toast.loading(t('project.feeds.aiLoading'));
    try {
      const response = await api.post(`/feeds/ai-suggestions/${project.id}/refresh`, undefined, { headers: { "X-Sketchario-AI-Refresh": "manual" } });
      setAiFeedItems(response.data || []);
      toast.success(t('project.feeds.aiSuccess'), { id: tid });
    } catch {
      toast.error(t('project.feeds.aiError'), { id: tid });
    }
    setRefreshingAi(false);
  };

  const filteredContents = filterContentsBySubTab(contents, contentSubTab);
  const derivedContentsByMaster = contents.reduce((groups, item) => {
    if (!item?.source_master_id) return groups;
    const masterId = item.source_master_id;
    groups[masterId] = [...(groups[masterId] || []), item];
    return groups;
  }, {});
  // In Master projects derivatives belong to their canonical Master. They
  // stay in state for resume/edit/render, but never appear as sibling cards.
  const visibleContents = isMasterProject
    ? filteredContents.filter(item => !item?.source_master_id)
    : filteredContents;
  const listGroups = [
    { key: 'published', label: t('status.published'), items: visibleContents.filter(c => c.status === 'published') },
    { key: 'scheduled', label: t('status.scheduled'), items: visibleContents.filter(c => c.status === 'scheduled') },
    { key: 'draft', label: t('status.draft'), items: visibleContents.filter(c => !c.status || c.status === 'draft') },
  ].filter(group => group.items.length > 0);

  if (loading) return <p className="text-[var(--text-muted)] text-center py-12">{t('common.loading')}</p>;

  const contentFilters = isMasterProject ? MASTER_CONTENT_FILTERS : SOCIAL_CONTENT_FILTERS;

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <div className={`flex items-center gap-3 px-4 md:px-6 py-3 border-b border-[var(--border-color)] flex-shrink-0 ${isMobile ? 'flex-wrap' : ''}`}>
        <button className="btn-ghost p-1.5" onClick={() => setActiveView('dashboard')} data-testid="back-to-dashboard"><ArrowLeft size={18} /></button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold gradient-text truncate" data-testid="project-title">{project.name}</h1>
          <p className="text-[10px] md:text-xs text-[var(--text-secondary)]">{project.sector} | {isMasterProject ? contents.filter(item => !item?.source_master_id).length : contents.length} {t('dashboard.contents')}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isMasterProject && (
            <button data-testid="new-post-btn" className="btn-gradient text-xs md:text-sm" onClick={() => setShowNewPost(true)}>
              <Plus weight="bold" size={14} /> <span className="hidden sm:inline">{t('project.content.new')}</span> {t('project.content.newPost')}
            </button>
          )}
          {!isMasterProject && (
            <button className="btn-ghost text-xs md:text-sm" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/${project.id}/csv`, '_blank')} data-testid="export-csv-btn">
              <Download size={14} /> <span className="hidden sm:inline">CSV</span>
            </button>
          )}
          <button
            className="btn-ghost text-xs p-1.5"
            onClick={() => setShowRightPanel(!showRightPanel)}
            data-testid="toggle-right-panel"
            title={t('project.queue.queueAnalytics')}
          >
            <ListBullets size={16} />
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden relative">
        <div className="flex-1 flex flex-col overflow-hidden">
          {primaryTab === 'content' && (
            <div className="px-4 md:px-6 pt-3 pb-2 flex-shrink-0">
              <div
                data-testid="content-filter-bar"
                className="flex gap-2 overflow-x-auto rounded-2xl border p-1.5"
                style={{
                  scrollbarWidth: 'none',
                  WebkitOverflowScrolling: 'touch',
                  background: 'linear-gradient(135deg, rgba(124,92,255,0.12), rgba(8,13,27,0.64) 48%, rgba(230,70,170,0.08))',
                  borderColor: 'rgba(124,92,255,0.18)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 14px 40px rgba(0,0,0,0.18)',
                }}
              >
                {contentFilters.map(filter => {
                  const Icon = filter.icon;
                  const active = contentSubTab === filter.id;
                  return (
                    <button
                      key={filter.id}
                      data-testid={`content-filter-${filter.id}`}
                      className={`group relative flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-semibold transition-all whitespace-nowrap ${active ? 'text-white' : 'text-[var(--text-muted)] hover:text-white hover:bg-white/5'}`}
                      style={active ? {
                        background: 'linear-gradient(135deg, rgba(124,92,255,0.92), rgba(224,64,176,0.72))',
                        boxShadow: '0 10px 28px rgba(124,92,255,0.24)',
                      } : {}}
                      onClick={() => setContentSubTab(filter.id)}
                    >
                      <span
                        className="flex h-7 w-7 items-center justify-center rounded-lg transition-colors"
                        style={{ background: active ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.05)' }}
                      >
                        <Icon weight={active ? 'fill' : 'regular'} size={16} />
                      </span>
                      <span>{t(filter.labelKey)}</span>
                    </button>
                  );
                })}
                <div className="ml-auto flex items-center pl-2">
                  <ContextHelpButton articleId="content-board" label="Guida Content Board" />
                </div>
                {contentSubTab !== 'calendar' && (
                  <div className="flex items-center gap-0.5 ml-1 border-l border-white/10 pl-2 flex-shrink-0">
                    <button
                      className={`p-2 rounded-xl transition-colors ${viewMode === 'cards' ? 'bg-white/10 text-white' : 'text-[var(--text-muted)] hover:text-white hover:bg-white/5'}`}
                      onClick={() => setViewMode('cards')}
                      title={t('project.content.viewCardsTitle')}
                    >
                      <SquaresFour size={15} />
                    </button>
                    <button
                      className={`p-2 rounded-xl transition-colors ${viewMode === 'list' ? 'bg-white/10 text-white' : 'text-[var(--text-muted)] hover:text-white hover:bg-white/5'}`}
                      onClick={() => setViewMode('list')}
                      title={t('project.content.viewListTitle')}
                    >
                      <ListBullets size={15} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto overscroll-contain px-4 md:px-6 pb-4">
            {primaryTab === 'content' && contentsLoadError && (
              <div className="text-center py-12">
                <p className="text-[var(--accent-pink)] mb-4">{resolveMasterProjectErrorMessage(contentsLoadError, t)}</p>
                <button className="btn-gradient text-sm" onClick={loadContents} data-testid="retry-load-contents-btn">
                  {t('wizard.actions.retry')}
                </button>
              </div>
            )}

            {primaryTab === 'content' && !contentsLoadError && contentSubTab !== 'calendar' && viewMode === 'cards' && (
              <ContentCardsView
                contents={visibleContents}
                derivedContentsByMaster={derivedContentsByMaster}
                queueItems={queueItems}
                isMobile={isMobile}
                openContentDetail={openContentDetail}
                deletingContentId={deletingContentId}
                deleteContent={deleteContent}
                setShowNewPost={setShowNewPost}
                isMasterProject={isMasterProject}
              />
            )}

            {primaryTab === 'content' && !contentsLoadError && contentSubTab !== 'calendar' && viewMode === 'list' && (
              <ContentListView
                groups={listGroups}
                derivedContentsByMaster={derivedContentsByMaster}
                collapsedGroups={collapsedGroups}
                toggleGroupCollapse={toggleGroupCollapse}
                queueItems={queueItems}
                openContentDetail={openContentDetail}
                toggleUrgent={toggleUrgent}
                deletingContentId={deletingContentId}
                deleteContent={deleteContent}
                isMasterProject={isMasterProject}
              />
            )}

            {primaryTab === 'content' && contentSubTab === 'calendar' && !contentsLoadError && (
              <>
                {publishQuota && publishQuota.unlimited === false && (
                  <div
                    className={`card mb-3 flex items-center justify-between gap-3 flex-wrap ${publishQuota.remaining === 0 ? 'border-orange-400' : ''}`}
                    data-testid="publish-quota-banner"
                  >
                    <p className="text-sm text-[var(--text-secondary)]">
                      {publishQuota.remaining === 0
                        ? t('project.calendar.quotaExhausted', { limit: publishQuota.monthly_limit })
                        : t('project.calendar.quotaRemaining', { used: publishQuota.used, limit: publishQuota.monthly_limit, remaining: publishQuota.remaining })}
                    </p>
                    {publishQuota.remaining === 0 && (
                      <button className="btn-gradient text-xs py-1.5 px-3" onClick={() => setActiveView('billing')}>
                        {t('project.calendar.quotaUpgradeCta')}
                      </button>
                    )}
                  </div>
                )}
                <ContentCalendarView
                contents={contents}
                queueItems={queueItems}
                calYear={calYear}
                calMonth={calMonth}
                setCalYear={setCalYear}
                setCalMonth={setCalMonth}
                isMasterProject={isMasterProject}
                projectCreatedAt={project.created_at}
                onMoveMasterEvent={moveMasterContentDayOffset}
                handleDrop={handleDrop}
                setDragContent={setDragContent}
                openContentDetail={openContentDetail}
                onMoveScheduledEvent={moveScheduledEvent}
                />
              </>
            )}

            {primaryTab === 'dna' && (
              <DnaWorkspace api={api} project={project} initialSection={initialSubTab} />
            )}

            {primaryTab === 'connections' && (
              <ConnectionsWorkspace api={api} project={project} initialSection={initialSubTab} />
            )}

            {primaryTab === 'media' && (
              <MediaLibraryTab api={api} project={project} />
            )}

            {primaryTab === 'analytics' && canAnalytics && (
              <div className="pt-2 pb-8">
                <Analytics project={project} />
              </div>
            )}

            {primaryTab === 'feeds' && (
              <div className="pt-2 space-y-6" data-testid="feeds-page">
                <div className="relative">
                  {!canRssFeeds && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center p-4">
                      <div className="rounded-full border border-[var(--gradient-start)]/40 bg-[rgba(8,10,18,0.88)] px-4 py-2 text-xs font-semibold text-white backdrop-blur-sm">
                        {t('project.feeds.proOnly')}
                      </div>
                    </div>
                  )}
                  <div className={!canRssFeeds ? 'opacity-40 pointer-events-none select-none' : ''}>
                  <div className="flex items-center justify-between mb-3" data-testid="feeds-news-section">
                    <p className="text-sm font-semibold flex items-center gap-2"><RssSimple size={16} /> {t('project.feeds.newsReddit')} ({t('project.feeds.beta')}) — {project.sector}</p>
                    <div className="flex items-center gap-2">
                      <ContextHelpButton articleId="feeds" label="Guida Feed" />
                      <button className="text-xs text-[var(--text-muted)] hover:text-white flex items-center gap-1" disabled={!canRssFeeds || refreshingFeeds} onClick={refreshFeeds}>
                      <ArrowClockwise size={12} className={refreshingFeeds ? 'animate-spin' : ''} /> {refreshingFeeds ? '...' : t('project.feeds.refresh')}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {(canRssFeeds ? feedItems.slice(0, 8) : lockedFeedPlaceholders).map(item => (
                      <div key={item.id || `rss-placeholder-${item}`} className="card cursor-pointer hover:border-[var(--gradient-start)] transition-colors relative" onClick={() => canRssFeeds && setSelectedFeedItem({ ...item, _type: 'rss' })}>
                        {canRssFeeds && pinnedItemIds.has(item.id) && <span className="absolute top-2 right-2 text-[10px]">📌</span>}
                        <p className="text-sm font-medium mb-1 pr-5">{canRssFeeds ? item.title : t('project.feeds.lockedTitle')}</p>
                        <p className="text-xs text-[var(--text-muted)]">{canRssFeeds ? item.feed_name : t('project.feeds.lockedSubtitle')}</p>
                      </div>
                    ))}
                    {canRssFeeds && feedItemsLoading && (
                      <p className="text-sm text-[var(--text-muted)]">{t('project.feeds.loadingItems')}</p>
                    )}
                    {canRssFeeds && !feedItemsLoading && feedItemsError && (
                      <p className="text-sm text-[var(--text-muted)]">{t('project.feeds.itemsError')}</p>
                    )}
                    {canRssFeeds && !feedItemsLoading && !feedItemsError && feedItems.length === 0 && (
                      <div className="text-sm text-[var(--text-muted)]">
                        <p>{t('project.feeds.itemsEmptyTitle')}</p>
                        <p>{t('project.feeds.itemsEmptyHint')}</p>
                      </div>
                    )}
                  </div>
                  </div>
                </div>
                <div className="relative">
                  {!canRssFeeds && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center p-4">
                      <div className="rounded-full border border-[var(--gradient-start)]/40 bg-[rgba(8,10,18,0.88)] px-4 py-2 text-xs font-semibold text-white backdrop-blur-sm">
                        {t('project.feeds.proOnly')}
                      </div>
                    </div>
                  )}
                  <div className={!canRssFeeds ? 'opacity-40 pointer-events-none select-none' : ''}>
                  <div className="flex items-center justify-between mb-3" data-testid="feeds-ai-section">
                    <p className="text-sm font-semibold flex items-center gap-2"><Sparkle size={16} weight="fill" /> {t('project.feeds.aiSuggestions')} — {project.sector}</p>
                    <button className="text-xs text-[var(--text-muted)] hover:text-white flex items-center gap-1" disabled={!canRssFeeds || refreshingAi} onClick={refreshAiSuggestions}>
                      <ArrowClockwise size={12} className={refreshingAi ? 'animate-spin' : ''} /> {refreshingAi ? '...' : t('editor.regenerate')}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(canRssFeeds ? aiFeedItems : lockedFeedPlaceholders).map(item => (
                      <div key={item.id || `ai-placeholder-${item}`} className="card cursor-pointer hover:border-[var(--accent-purple)] transition-colors relative"
                        style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(236,72,153,0.08) 100%)', border: '1px solid rgba(99,102,241,0.15)' }}
                        onClick={() => canRssFeeds && setSelectedFeedItem({ ...item, _type: 'ai' })}>
                        {canRssFeeds && pinnedItemIds.has(item.id) && <span className="absolute top-2 right-2 text-[10px]">📌</span>}
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className={`w-2 h-2 rounded-full ${canRssFeeds && item.format === 'reel' ? 'bg-[var(--accent-pink)]' : 'bg-[var(--gradient-start)]'}`} />
                          <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase">{canRssFeeds && item.format ? t(`format.${item.format}`) : t('format.reel')}</span>
                          {canRssFeeds && item.trend_tag && <span className="text-[10px] text-[var(--accent-purple)]">#{item.trend_tag}</span>}
                        </div>
                        <p className="text-sm font-medium mb-1 pr-5">{canRssFeeds ? item.title : t('project.feeds.lockedAiTitle')}</p>
                        <p className="text-xs text-[var(--text-muted)]">{canRssFeeds ? item.summary : t('project.feeds.lockedAiSubtitle')}</p>
                      </div>
                    ))}
                    {canRssFeeds && aiFeedItems.length === 0 && <p className="text-sm text-[var(--text-muted)]">{t('project.feeds.aiEmpty')}</p>}
                  </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <FeedStrips
            isMobile={isMobile}
            project={project}
            feedItems={feedItems}
            feedItemsLoading={feedItemsLoading}
            feedItemsError={feedItemsError}
            aiFeedItems={aiFeedItems}
            pinnedItemIds={pinnedItemIds}
            refreshingFeeds={refreshingFeeds}
            refreshingAi={refreshingAi}
            refreshFeeds={refreshFeeds}
            refreshAiSuggestions={refreshAiSuggestions}
            setSelectedFeedItem={setSelectedFeedItem}
            canRssFeeds={canRssFeeds}
            t={t}
          />
        </div>

        <AnimatePresence>
          {showRightPanel && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40" style={{ background: 'rgba(0,0,0,0.4)' }}
              onClick={() => setShowRightPanel(false)} />
          )}
        </AnimatePresence>

        <motion.div
          animate={{ x: showRightPanel ? 0 : rightPanelWidth - 24 }}
          initial={false}
          transition={{ type: 'tween', duration: 0.25 }}
          className="fixed top-0 right-0 bottom-0 z-50 flex"
          style={{ width: rightPanelWidth }}
        >
          <div
            className="flex flex-col items-center justify-center w-6 cursor-pointer flex-shrink-0 select-none"
            style={{ background: 'var(--bg-card)', borderLeft: '1px solid var(--border-color)', borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)', borderRadius: '8px 0 0 8px' }}
            onClick={() => setShowRightPanel(!showRightPanel)}
            onMouseDown={e => {
              if (e.button !== 0) return;
              e.preventDefault();
              const startX = e.clientX;
              const startW = rightPanelWidth;
              const onMove = (ev) => { const diff = startX - ev.clientX; setRightPanelWidth(Math.max(220, Math.min(600, startW + diff))); };
              const onUp = () => { document.body.style.cursor = ''; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
              document.body.style.cursor = 'col-resize';
              window.addEventListener('mousemove', onMove);
              window.addEventListener('mouseup', onUp);
            }}
          >
            <div className="flex flex-col gap-1">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-1 h-1 rounded-full" style={{ background: 'var(--text-muted)' }} />
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4" style={{ background: 'var(--bg-secondary)', borderLeft: '1px solid var(--border-color)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold">{t('project.queue.queueAnalytics')}</h3>
              <button className="btn-ghost p-1.5" onClick={() => setShowRightPanel(false)}><X size={16} /></button>
            </div>
            <RightPanelContent
              queueItems={queueItems}
              contents={contents}
              cancelQueueItem={cancelQueueItem}
              cancellingQueueId={cancellingQueueId}
              clearQueue={clearQueue}
              clearingQueue={clearingQueue}
              project={project}
              canAnalytics={canAnalytics}
            />
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {!isMasterProject && showNewPost && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-4" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => !newPostLoading && setShowNewPost(false)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="card w-full max-w-lg" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-4">{t('project.content.generate')}</h3>
              <div className="space-y-4">
                <textarea data-testid="new-post-hook" className="input-dark" rows={3} placeholder={t('project.content.hookPlaceholder')} value={newPostHook} onChange={e => setNewPostHook(e.target.value)} style={{ paddingLeft: '1rem' }} />
                <div className="flex gap-2">
                  <button className={`preset-btn flex-1 ${newPostFormat === 'reel' ? 'active' : ''}`} onClick={() => setNewPostFormat('reel')}><Video size={14} /> {t('format.reel')}</button>
                  <button className={`preset-btn flex-1 ${newPostFormat === 'carousel' ? 'active' : ''}`} onClick={() => setNewPostFormat('carousel')}><Image size={14} /> {t('format.carousel')}</button>
                  <button className={`preset-btn flex-1 ${newPostFormat === 'prompted_reel' ? 'active' : ''}`} onClick={() => setNewPostFormat('prompted_reel')}>🤖 {t('format.prompted_reel')}</button>
                </div>
                <div className="flex gap-2">
                  <button className={`preset-btn flex-1 ${!newPostUseAi ? 'active' : ''}`} onClick={() => setNewPostUseAi(false)}>{t('project.content.fromScratch')}</button>
                  <button className={`preset-btn flex-1 ${newPostUseAi ? 'active' : ''}`} onClick={() => setNewPostUseAi(true)}><Sparkle size={14} /> {t('project.content.withAi')}</button>
                </div>
                <div className="flex gap-3">
                  <button className="btn-ghost flex-1" onClick={() => setShowNewPost(false)}>{t('common.cancel')}</button>
                  <button data-testid="create-post-btn" className="btn-gradient flex-1" onClick={createNewPost} disabled={newPostLoading || !newPostHook.trim()}>
                    {newPostLoading ? '...' : t('common.save')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedContent && (
          isCanonicalMasterContent(selectedContent)
            ? <MasterContentDetail
                content={selectedContent}
                project={project}
                api={api}
                onClose={() => setSelectedContent(null)}
                onUpdate={handleContentUpdate}
                onDerived={handleMasterDerived}
                existingCarousel={contents
                  .filter(item => item.origin === 'master_derivation' && item.format === 'carousel' && item.source_master_id === selectedContent.id && item.source_master_revision === selectedContent.master_revision)
                  .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))[0]}
                existingReel={contents
                  .filter(item => item.origin === 'master_derivation' && item.format === 'reel' && item.source_master_id === selectedContent.id && item.source_master_revision === selectedContent.master_revision)
                  .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))[0]}
                existingPromptedReel={contents
                  .filter(item => item.origin === 'master_derivation' && item.format === 'prompted_reel' && item.source_master_id === selectedContent.id && item.source_master_revision === selectedContent.master_revision)
                  .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))[0]}
              />
            : <ContentDetail content={selectedContent} project={project} onClose={() => setSelectedContent(null)} onUpdate={handleContentUpdate} />
        )}

        {selectedFeedItem && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
            onClick={() => setSelectedFeedItem(null)}>
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              className="w-full md:max-w-2xl rounded-t-2xl md:rounded-2xl overflow-hidden flex flex-col"
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', maxHeight: '85vh' }}
              onClick={e => e.stopPropagation()}>
              <div className="flex items-start justify-between p-4 md:p-5 border-b border-[var(--border-color)] flex-shrink-0">
                <div className="flex-1 min-w-0 pr-3">
                  {selectedFeedItem._type === 'ai' && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="badge purple text-[10px]">✨ {t('project.feeds.aiSuggestions')}</span>
                      {selectedFeedItem.format && <span className="badge text-[10px]" style={{ background: 'var(--bg-card)' }}>{t(`format.${selectedFeedItem.format}`)}</span>}
                      {selectedFeedItem.trend_tag && <span className="text-[10px] text-[var(--accent-purple)]">#{selectedFeedItem.trend_tag}</span>}
                    </div>
                  )}
                  {selectedFeedItem._type === 'rss' && selectedFeedItem.feed_name && (
                    <p className="text-[10px] text-[var(--text-muted)] mb-1 uppercase font-semibold">{selectedFeedItem.feed_name}</p>
                  )}
                  <h2 className="text-base font-bold leading-snug">{selectedFeedItem.title}</h2>
                </div>
                <button className="btn-ghost p-1.5 flex-shrink-0" onClick={() => setSelectedFeedItem(null)}><X size={18} /></button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 md:p-5">
                {selectedFeedItem.summary && (
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4" style={{ whiteSpace: 'pre-wrap' }}>{selectedFeedItem.summary}</p>
                )}
                {selectedFeedItem.link && (
                  <a href={selectedFeedItem.link} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-[var(--accent-purple)] hover:underline flex items-center gap-1 mb-4">
                    <Globe size={12} /> {t('project.feeds.originalArticle')} →
                  </a>
                )}
                {selectedFeedItem.published && (
                  <p className="text-[10px] text-[var(--text-muted)]">{t('project.feeds.publishedAt')}: {selectedFeedItem.published}</p>
                )}
              </div>

              <div className="flex items-center gap-3 p-4 border-t border-[var(--border-color)] flex-shrink-0" style={{ background: 'var(--bg-secondary)' }}>
                <button
                  className={`btn-ghost text-xs py-2 px-3 flex items-center gap-1.5 ${pinnedItemIds.has(selectedFeedItem.id) ? 'text-[var(--accent-purple)]' : ''}`}
                  disabled={pinningItemId === selectedFeedItem.id}
                  onClick={() => pinFeedItem(selectedFeedItem, selectedFeedItem._type)}
                  title={pinnedItemIds.has(selectedFeedItem.id) ? t('project.feeds.unsave') : t('project.feeds.pinSaved')}
                >
                  {pinningItemId === selectedFeedItem.id
                    ? <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    : <span>{pinnedItemIds.has(selectedFeedItem.id) ? '📌' : '📍'}</span>}
                  {pinnedItemIds.has(selectedFeedItem.id) ? t('project.feeds.saved') : t('project.feeds.save')}
                </button>
                <button
                  className="btn-gradient flex-1 text-sm py-2"
                  disabled={feedLoading}
                  onClick={() => { setSelectedFeedItem(null); feedToPost(selectedFeedItem); }}
                >
                  {feedLoading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block mr-2" />{t('project.content.generating')}</> : `✦ ${t('project.feeds.createPost')} →`}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
